import { gql } from '@apollo/client';

/** Reach (Calendar · Campaigns · Channels): queries, channel names and dates. */

export const REACH_POST_FIELDS = `
  id
  platform
  summary
  title
  reachStatus
  reviewStatus
  scheduleTime
  proposedTime
  socialPublishDate
  socialProfileId
`;

export const REACH_CALENDAR = gql`
  query ReachCalendar($accountId: ID!, $from: String!, $to: String!) {
    reachCalendar(accountId: $accountId, from: $from, to: $to) {
      posts { ${REACH_POST_FIELDS} }
      waiting { ${REACH_POST_FIELDS} }
    }
  }
`;

export const APPROVE_SOCIAL_POST = gql`
  mutation ApproveSocialPost($id: ID!) {
    approveSocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

export const SKIP_SOCIAL_POST = gql`
  mutation SkipSocialPost($id: ID!) {
    skipSocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

export const APPROVE_CAMPAIGN = gql`
  mutation ApproveCampaign($id: ID!) {
    approveCampaign(id: $id) {
      approved
      scheduled
      undated
      skippedChannels
    }
  }
`;

export const REACH_CHANNELS = gql`
  query ReachChannels($accountId: ID!) {
    socialProfiles(accountId: $accountId) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
    }
  }
`;

/** Connect the channels picked from a sign-in (the ticket came from the API's exchange). */
export const CONNECT_SOCIAL_CHANNELS = gql`
  mutation ConnectSocialChannels($input: ConnectSocialChannelsInput!) {
    connectSocialChannels(input: $input) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
    }
  }
`;

/**
 * Reach → Channels "Disconnect": the stored credentials are removed; the row
 * stays, inactive, so its posts keep their channel. (Settings → Social's
 * "Active" switch is a pause through UPDATE_SOCIAL_PROFILE, credentials kept.)
 */
export const DISCONNECT_SOCIAL_CHANNEL = gql`
  mutation DisconnectSocialChannel($id: ID!) {
    disconnectSocialChannel(id: $id) {
      id
      isActive
      isPublishing
    }
  }
`;

/** A person's own post (Reach → Calendar → Write a post): one call per channel. */
export const CREATE_REACH_POST = gql`
  mutation CreateReachPost($input: CreateSocialPostInput!) {
    createSocialPost(input: $input) { ${REACH_POST_FIELDS} }
  }
`;

export const SET_APPROVAL_MODE = gql`
  mutation SetSocialProfileApprovalMode($id: ID!, $mode: String!) {
    setSocialProfileApprovalMode(id: $id, mode: $mode) {
      id
      approvalMode
    }
  }
`;

export interface ReachPost {
  id: string;
  platform: string;
  summary?: string | null;
  title?: string | null;
  reachStatus: string;
  reviewStatus?: string | null;
  scheduleTime?: string | null;
  proposedTime?: string | null;
  socialPublishDate?: string | null;
  socialProfileId: string;
}

/** An always-on campaign's conditions (apiserver reach/services/alwaysOn.js). */
export interface AlwaysOnConditions {
  priority?: number;
  postType?: 'auto' | 'link' | 'text' | 'photo' | 'video' | 'random' | string;
  maxAgeHours?: number;
  delayHours?: number;
  immediate?: boolean;
  schedule?: Array<{ time?: string; minuteMark?: number; intervalMinutes?: number }>;
  windows?: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    spansMidnight?: boolean;
  }>;
  include?: string[];
  exclude?: string[];
  mediaTypes?: string[];
  /** Only on campaigns migrated from the old rules; read-only. */
  filterSql?: string;
}

export interface Channel {
  id: string;
  platform: string;
  profileId?: string | null;
  profileName?: string | null;
  profileUsername?: string | null;
  profileLink?: string | null;
  isActive: boolean;
  isPublishing?: boolean | null;
  approvalMode: 'ask' | 'auto' | string;
}

/** Platforms we can post to, in the order Add a channel shows them. */
export const POSTABLE = [
  'x',
  'threads',
  'instagram',
  'facebook',
  'linkedin',
  'youtube',
  'tiktok',
] as const;

const NAMES: Record<string, [string, string]> = {
  x: ['X', 'X'],
  twitter: ['X', 'X'],
  threads: ['Threads', '@'],
  instagram: ['Instagram', 'IG'],
  facebook: ['Facebook', 'f'],
  youtube: ['YouTube', 'YT'],
  tiktok: ['TikTok', 'TT'],
  linkedin: ['LinkedIn', 'in'],
  snapchat: ['Snapchat', 'SC'],
  bluesky: ['Bluesky', 'B'],
};
export const channelName = (p: string) => NAMES[p?.toLowerCase()]?.[0] || p;
export const channelMark = (p: string) =>
  NAMES[p?.toLowerCase()]?.[1] || p?.[0]?.toUpperCase() || '?';

/** Dates arrive as ISO strings or epoch milliseconds (as strings). */
export function toDate(v?: string | null): Date | null {
  if (!v) return null;
  const d = /^\d{10,}$/.test(v) ? new Date(Number(v)) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** When the calendar places a post: when it went out, will go out, or is proposed to. */
export const postTime = (p: ReachPost) =>
  toDate(p.socialPublishDate) || toDate(p.scheduleTime) || toDate(p.proposedTime);

export const postText = (p: ReachPost) => (p.summary || p.title || '').trim();

export const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
export const dayTime = (d: Date) =>
  d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
